import React, { useEffect } from "react";
import { useCurrentUser } from "actions/CurrentUserActions";
import { Redirect } from "react-router-dom";
import { AppPath } from "components/AppPath";
import { CurrentUserLoadingState } from "reducers/CurrentUserReducer";
import AppLoading from "components/AppLoading";
import AppLayoutWrapper from "components/AppLayoutWrapper";
import { Heading16, Heading32 } from "components/shared/Heading";
import { FeedbackButton } from "components/shared/FeedbackButton";
import BrowserEnforcementPage from "components/BrowserEnforcementPage";
import DesktopEnforcementPage from "components/DesktopEnforcementPage";
import { shouldAllowStudioAccessForThisBrowser } from "utilities/BrowserUtilities";

const ForceLogin = (): JSX.Element => {
  useEffect(() => {
    if (!window.adobeIMS?.isSignedInUser()) {
      window.adobeIMS?.signIn();
    }
  });

  return <></>;
};

export const AwaitAuth: React.FC = ({ children }) => {
  const { currentUserLoadingState } = useCurrentUser();

  switch (currentUserLoadingState) {
    case CurrentUserLoadingState.NOT_LOADED:
    case CurrentUserLoadingState.LOADING:
      return <AppLoading />;
    case CurrentUserLoadingState.FAILED:
      return <LoginFailed />;
    case CurrentUserLoadingState.LOADED: {
      return <>{children}</>;
    }
  }
};

const RequireAuthInternal: React.FC = ({ children }) => {
  const { anonymousAllowedCurrentUser: currentUser } = useCurrentUser();
  const isLoggedIn = !!currentUser && !currentUser.isAnonymous;
  if (isLoggedIn) {
    return <>{children}</>;
  } else {
    return <ForceLogin />;
  }
};

export const RequireAuth: React.FC = ({ children }) => {
  return (
    <AwaitAuth>
      <RequireAuthInternal>{children}</RequireAuthInternal>
    </AwaitAuth>
  );
};

export const EnforceBrowser: React.FC = ({ children }) => {
  const { anonymousAllowedCurrentUser: currentUser } = useCurrentUser();
  const allowStudioAccessForThisBrowser =
    shouldAllowStudioAccessForThisBrowser();
  const enableAllBrowserAccess = !!currentUser?.hasAllBrowserStudioAccess;

  if (allowStudioAccessForThisBrowser || enableAllBrowserAccess) {
    return <>{children}</>;
  } else {
    return <BrowserEnforcementPage />;
  }
};

export const EnforceDesktop: React.FC<{ page?: React.ComponentType }> = ({
  children,
  page,
}) => {
  const isMobile = navigator.userAgent.indexOf("Mobile") !== -1;
  const EnforcementPage = page ?? DesktopEnforcementPage;

  if (isMobile) {
    return <EnforcementPage />;
  } else {
    return <>{children}</>;
  }
};

const RedirectIfNotAuthedInternal: React.FC<{ to?: string }> = ({
  children,
  to,
}) => {
  const { anonymousAllowedCurrentUser: currentUser } = useCurrentUser();

  const isLoggedIn = !!currentUser && !currentUser.isAnonymous;
  if (isLoggedIn) {
    return <>{children}</>;
  } else {
    return <Redirect to={to ?? AppPath.ROOT} />;
  }
};

export const RedirectIfNotAuthed: React.FC<{ to?: string }> = ({
  children,
  to,
}) => {
  return (
    <AwaitAuth>
      <RedirectIfNotAuthedInternal to={to}>
        {children}
      </RedirectIfNotAuthedInternal>
    </AwaitAuth>
  );
};

const LoginFailed = (): React.ReactElement => {
  return (
    <AppLayoutWrapper>
      <Heading32>{I18n.t("login.failed.title")}</Heading32>
      <Heading16>{I18n.t("login.failed.description1")}</Heading16>
      <Heading16>{I18n.t("login.failed.description2")}</Heading16>
      <br />
      <sp-button size="xl" variant="accent" onClick={window.adobeIMS?.signIn}>
        {I18n.t("login.sign_in")}
      </sp-button>
      <FeedbackButton />
    </AppLayoutWrapper>
  );
};
